import { DEFAULT_API_BASE_URL } from '@/lib/constants';

const ADMIN_ACCESS_PATH = '/api/v1/admin/access';
const ADMIN_HUMAN_REVIEW_REQUESTS_PATH = '/api/v1/admin/human-review-requests';
const ADMIN_TASK_STATUS_PATH = '/api/v1/admin/tasks/status';
const ADMIN_ATTEMPTS_PATH = '/api/v1/admin/attempts';
const ATTEMPT_ACTIVITY_PAGE_SIZE = 50;

const ATTEMPT_ACTIVITY_SOURCES = {
  ATTEMPT_ACTIVITY_SOURCE_SUPERVISOR: 'supervisor',
  ATTEMPT_ACTIVITY_SOURCE_PROVIDER: 'provider',
  ATTEMPT_ACTIVITY_SOURCE_TOOL: 'tool',
  ATTEMPT_ACTIVITY_SOURCE_SEARCH: 'search',
  ATTEMPT_ACTIVITY_SOURCE_AGENT: 'agent',
  ATTEMPT_ACTIVITY_SOURCE_HOOK: 'hook',
  ATTEMPT_ACTIVITY_SOURCE_SUBPROCESS: 'subprocess',
  ATTEMPT_ACTIVITY_SOURCE_COORDINATOR: 'coordinator',
} as const;

const ATTEMPT_ACTIVITY_CAPTURE_LEVELS = {
  ATTEMPT_ACTIVITY_CAPTURE_LEVEL_STANDARD: 'standard',
  ATTEMPT_ACTIVITY_CAPTURE_LEVEL_DEBUG: 'debug',
} as const;

const ATTEMPT_CONCLUSIONS = {
  ATTEMPT_CONCLUSION_ACCEPTED: 'accepted',
  ATTEMPT_CONCLUSION_RETRY_REQUIRED: 'retry_required',
  ATTEMPT_CONCLUSION_LEASE_RECLAIMED: 'lease_reclaimed',
  accepted: 'accepted',
  retry_required: 'retry_required',
  lease_reclaimed: 'lease_reclaimed',
} as const;

export const ADMIN_HTTP_STATUS = {
  OK: 200,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
} as const;

export type AdminAccessStatus =
  (typeof ADMIN_HTTP_STATUS)[keyof typeof ADMIN_HTTP_STATUS];

// The folded projection of a request's decision history.
export type HumanReviewRequestState =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'changes_requested'
  | 'expired';

// A decision a human may record. `reopened` is valid only on an expired request.
export type HumanReviewDecisionResult =
  | 'approved'
  | 'rejected'
  | 'changes_requested'
  | 'reopened';

// Every result the decision log can hold, including the ones only the SLA
// reaper writes. `expired` is recorded, never client-submitted.
export type HumanReviewResult = HumanReviewDecisionResult | 'expired';

// What produced the latest decision, derived server-side (never client-set):
// a person, an automated policy gate, or the system (SLA expiry).
export type DecisionSource = 'human' | 'policy' | 'system';

export type HumanReviewSort = 'newest' | 'oldest';

export type HumanReviewRequestStateFilter = HumanReviewRequestState | 'all';

// The full toolbar filter state, threaded through the query key so a change
// refetches (and re-facets) the list. null = "all" for the optional dimensions.
export type HumanReviewRequestFilter = {
  state: HumanReviewRequestStateFilter;
  subjectType: string | null;
  priority: string | null;
  decisionSource: DecisionSource | null;
  sort: HumanReviewSort;
};

// One immutable row of the append-only decision log. `sequence` is monotonic
// per request and is the optimistic-concurrency guard.
export type HumanReviewDecision = {
  id: string;
  humanReviewRequestId: string;
  sequence: number;
  result: HumanReviewResult;
  source: DecisionSource;
  decidedByUserId?: string | null;
  decidedByService?: string | null;
  decidedAt: string;
  reason: string;
};

// Who to credit for a decision. The contract splits the actor by kind — a person
// carries decidedByUserId, an automated policy or the SLA reaper carries
// decidedByService — so display collapses whichever one is set.
export function humanReviewDecisionActor(decision: {
  decidedByUserId?: string | null;
  decidedByService?: string | null;
  source: DecisionSource;
}): string {
  return (
    decision.decidedByUserId ?? decision.decidedByService ?? decision.source
  );
}

export type HumanReviewSubjectType =
  | 'attempt'
  | 'dirty_worktree'
  | 'suppression'
  | 'finding'
  | 'finding_verdict_delivery';

// One request for human judgment. Exactly one typed subject shape is populated,
// selected by `subjectType`; `payload` is display-only.
export type HumanReviewRequest = {
  id: string;
  subjectType: HumanReviewSubjectType;
  attemptId?: string;
  dirtyWorktreeRepositoryId?: string;
  dirtyWorktreePath?: string;
  suppressionId?: string;
  findingReviewId?: string;
  findingId?: string;
  findingFilePath?: string;
  findingVerdictDeliveryId?: number;
  payload: Record<string, unknown>;
  state: HumanReviewRequestState;
  priority: string;
  confidence?: number | null;
  clusterKey?: string | null;
  createdAt: string;
  updatedAt: string;
  latestSequence: number;
  decisionCount: number;
  latestDecision?: HumanReviewDecision | null;
  decisionSource: DecisionSource;
};

// The subject identity as display key/value pairs. The wire contract carries typed
// nullable columns (exactly one shape populated, selected by subjectType) rather than
// an authoritative JSON relationship, so the flat grounding map the trace and payload
// views render is derived here instead of being sent.
export function humanReviewSubjectRef(
  request: HumanReviewRequest
): Record<string, string | number> {
  const ref: Record<string, string | number> = {};
  const put = (key: string, value: string | number | undefined | null) => {
    if (value !== undefined && value !== null && value !== '') ref[key] = value;
  };
  switch (request.subjectType) {
    case 'attempt':
      put('attemptId', request.attemptId);
      break;
    case 'dirty_worktree':
      put('repositoryId', request.dirtyWorktreeRepositoryId);
      put('worktreePath', request.dirtyWorktreePath);
      break;
    case 'suppression':
      put('suppressionId', request.suppressionId);
      break;
    case 'finding':
      put('reviewId', request.findingReviewId);
      put('findingId', request.findingId);
      put('filePath', request.findingFilePath);
      break;
    case 'finding_verdict_delivery':
      put('findingVerdictDeliveryId', request.findingVerdictDeliveryId);
      break;
  }
  return ref;
}

// Drill-down facet counts: each dimension is counted with the OTHER active
// filters applied but excluding its own, so a facet chip shows the count you
// would get by selecting it.
export type HumanReviewFacets = {
  subjectType: Record<string, number>;
  state: Record<string, number>;
  priority: Record<string, number>;
  decisionSource: Record<string, number>;
};

export type HumanReviewRequestsResponse = {
  requests: HumanReviewRequest[];
  hasMore: boolean;
  nextCursor?: string;
  facets?: HumanReviewFacets;
};

export type HumanReviewDecisionResponse = {
  request: HumanReviewRequest;
  decision?: HumanReviewDecision;
};

export type HumanReviewRequestDetail = {
  request: HumanReviewRequest;
  decisions: HumanReviewDecision[];
};

export type HumanReviewRequestsResult =
  | { status: 'available'; data: HumanReviewRequestsResponse }
  | { status: 'disabled' };

export type HumanReviewRequestDetailResult =
  | { status: 'available'; data: HumanReviewRequestDetail }
  | { status: 'disabled' };

export type Repository = {
  repositoryId: string;
  originUrl: string;
  status: string;
  createdAt: string;
};

export type Epic = {
  epicId: string;
  repositoryId: string;
  version: number;
  goal: string;
  createdAt: string;
};

export type Task = {
  taskId: string;
  epicId: string;
  title: string;
  priority: number;
  specUri: string;
  createdAt: string;
  mode: string;
  inputs: string;
  dependsOn: string[];
  delivered: boolean;
};

export type AttemptState = 'leased' | 'in_progress' | 'submitted' | 'completed';

export type AttemptConclusion =
  | 'accepted'
  | 'retry_required'
  | 'lease_reclaimed';

export type TaskReadoutState =
  | 'running'
  | 'retrying'
  | 'ready'
  | 'waiting'
  | 'blocked'
  | 'done'
  | 'delivered';

export type TaskStateFilter = 'all' | TaskReadoutState;

export type Attempt = {
  attemptId: string;
  taskId: string;
  epicId: string;
  repositoryId: string;
  state: AttemptState;
  iteration: number;
  worktreePath: string;
  workerId: string;
  conclusion?: AttemptConclusion;
  conclusionReason: string;
  priorFeedback: string;
  result: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  runDurationMs?: number;
  prUrl: string;
  commitSha: string;
  createdAt: string;
  updatedAt: string;
};

export type AttemptTransition =
  | {
      state: 'submitted';
      headBranch: string;
      commitSha: string;
    }
  | {
      state: 'completed';
      conclusion: AttemptConclusion;
      conclusionReason?: string;
      result?: string;
    };

export type TaskStatusResponse = {
  repositories: Repository[];
  epics: Epic[];
  tasks: Task[];
  attempts: Attempt[];
  runnableTaskIds: string[];
};

export type TaskStatusResult =
  | { status: 'available'; data: TaskStatusResponse }
  | { status: 'disabled' };

export type AttemptActivity = {
  activityId: string;
  attemptId: string;
  taskId: string;
  sequence: number;
  activityType: string;
  actor: string;
  producerId: string;
  occurredAt: string;
  recordedAt: string;
  source:
    | (typeof ATTEMPT_ACTIVITY_SOURCES)[keyof typeof ATTEMPT_ACTIVITY_SOURCES]
    | '';
  outcome: string;
  parentEventId: string;
  traceId: string;
  spanId: string;
  captureLevel:
    | (typeof ATTEMPT_ACTIVITY_CAPTURE_LEVELS)[keyof typeof ATTEMPT_ACTIVITY_CAPTURE_LEVELS]
    | '';
  schemaVersion: number;
  payload: string;
  artifactUri: string;
  artifactSha256: string;
  redactionPolicy: string;
  redacted: boolean;
  retainUntil: string;
};

export type AttemptActivityPage = {
  activities: AttemptActivity[];
  hasMore: boolean;
  nextCursor: string;
};

export const ATTEMPT_ACTIVITY_EXPORT_STATES = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed',
  EXPIRED: 'expired',
} as const;

export type AttemptActivityExportState =
  (typeof ATTEMPT_ACTIVITY_EXPORT_STATES)[keyof typeof ATTEMPT_ACTIVITY_EXPORT_STATES];

export type AttemptActivityExport = {
  exportId: string;
  attemptId: string;
  state: AttemptActivityExportState;
  includePayload: boolean;
  highWaterSequence: number;
  failureCode?: string;
  requestedAt: string;
  completedAt?: string;
  expiresAt: string;
};

type AttemptActivityExportWire = Omit<
  AttemptActivityExport,
  'state' | 'highWaterSequence'
> & {
  state?: AttemptActivityExportState;
  highWaterSequence?: number | string;
};

type AttemptActivityWire = Omit<
  AttemptActivity,
  'sequence' | 'source' | 'captureLevel'
> & {
  sequence?: string;
  source?: keyof typeof ATTEMPT_ACTIVITY_SOURCES;
  captureLevel?: keyof typeof ATTEMPT_ACTIVITY_CAPTURE_LEVELS;
};

type AttemptActivityPageWire = {
  activities?: AttemptActivityWire[];
  hasMore?: boolean;
  nextCursor?: string;
};

type TaskStatusWireResponse = {
  repositories?: Array<{
    repositoryId?: string;
    originUrl?: string;
    status?: string;
    createdAt?: string;
  }>;
  epics?: Array<{
    epicId?: string;
    repositoryId?: string;
    version?: number;
    goal?: string;
    createdAt?: string;
  }>;
  tasks?: Array<{
    taskId?: string;
    epicId?: string;
    title?: string;
    priority?: number;
    specUri?: string;
    createdAt?: string;
    mode?: string;
    inputs?: string;
    dependsOn?: string[];
    delivered?: boolean;
  }>;
  attempts?: Array<{
    attemptId?: string;
    taskId?: string;
    epicId?: string;
    repositoryId?: string;
    state?: AttemptState;
    iteration?: number;
    worktreePath?: string;
    workerId?: string;
    conclusion?: string;
    conclusionReason?: string;
    priorFeedback?: string;
    result?: string;
    costUsd?: number;
    inputTokens?: number | string;
    outputTokens?: number | string;
    runDurationMs?: number | string;
    prUrl?: string;
    commitSha?: string;
    createdAt?: string;
    updatedAt?: string;
  }>;
  runnableTaskIds?: string[];
};

function finiteNumber(value: number | string | undefined): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function attemptConclusion(
  value: string | undefined
): AttemptConclusion | undefined {
  return value && value in ATTEMPT_CONCLUSIONS
    ? ATTEMPT_CONCLUSIONS[value as keyof typeof ATTEMPT_CONCLUSIONS]
    : undefined;
}

function finiteOptionalNumber(
  value: number | string | undefined
): number | undefined {
  if (value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const getBaseUrl = (): string => {
  if (
    typeof window === 'undefined' &&
    typeof process !== 'undefined' &&
    process.env?.INTEGRATION_BASE_URL
  ) {
    return process.env.INTEGRATION_BASE_URL;
  }
  if (
    typeof __MINDREALM_VITE_API_URL__ !== 'undefined' &&
    __MINDREALM_VITE_API_URL__
  ) {
    return __MINDREALM_VITE_API_URL__;
  }
  if (
    typeof window === 'undefined' &&
    typeof process !== 'undefined' &&
    process.env
  ) {
    return DEFAULT_API_BASE_URL;
  }
  return DEFAULT_API_BASE_URL;
};

export async function probeAdminAccess(token: string): Promise<number> {
  const url = new URL(ADMIN_ACCESS_PATH, getBaseUrl());

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === ADMIN_HTTP_STATUS.NOT_FOUND) {
    const fallbackUrl = new URL(ADMIN_HUMAN_REVIEW_REQUESTS_PATH, getBaseUrl());
    fallbackUrl.searchParams.set('limit', '1');

    const fallbackResponse = await fetch(fallbackUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return fallbackResponse.status;
  }

  return response.status;
}

async function adminFetch(token: string, path: string, init?: RequestInit) {
  const url = new URL(path, getBaseUrl());
  const response = await fetch(url, {
    ...init,
    ...(init?.method ? {} : { cache: 'no-store' }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  return response;
}

export async function getTaskStatus(
  token: string,
  originUrl?: string
): Promise<TaskStatusResult> {
  const query = new URLSearchParams();
  if (originUrl) query.set('originUrl', originUrl);
  const path = `${ADMIN_TASK_STATUS_PATH}${query.size ? `?${query}` : ''}`;
  const response = await adminFetch(token, path);
  if (response.status === ADMIN_HTTP_STATUS.NOT_FOUND) {
    return { status: 'disabled' };
  }
  if (!response.ok) {
    throw new Error(`Task status request failed with HTTP ${response.status}`);
  }
  const data = (await response.json()) as TaskStatusWireResponse;
  return {
    status: 'available',
    data: {
      repositories: (data.repositories ?? []).map((project) => ({
        repositoryId: project.repositoryId ?? '',
        originUrl: project.originUrl ?? '',
        status: project.status ?? '',
        createdAt: project.createdAt ?? '',
      })),
      epics: (data.epics ?? []).map((plan) => ({
        epicId: plan.epicId ?? '',
        repositoryId: plan.repositoryId ?? '',
        version: plan.version ?? 0,
        goal: plan.goal ?? '',
        createdAt: plan.createdAt ?? '',
      })),
      tasks: (data.tasks ?? []).map((task) => ({
        taskId: task.taskId ?? '',
        epicId: task.epicId ?? '',
        title: task.title ?? '',
        priority: task.priority ?? 0,
        specUri: task.specUri ?? '',
        createdAt: task.createdAt ?? '',
        mode: task.mode ?? '',
        inputs: task.inputs ?? '',
        dependsOn: task.dependsOn ?? [],
        delivered: task.delivered ?? false,
      })),
      attempts: (data.attempts ?? []).map((attempt) => ({
        attemptId: attempt.attemptId ?? '',
        taskId: attempt.taskId ?? '',
        epicId: attempt.epicId ?? '',
        repositoryId: attempt.repositoryId ?? '',
        state: attempt.state ?? 'leased',
        iteration: attempt.iteration ?? 0,
        worktreePath: attempt.worktreePath ?? '',
        workerId: attempt.workerId ?? '',
        conclusion: attemptConclusion(attempt.conclusion),
        conclusionReason: attempt.conclusionReason ?? '',
        priorFeedback: attempt.priorFeedback ?? '',
        result: attempt.result ?? '',
        costUsd: attempt.costUsd ?? 0,
        inputTokens: finiteNumber(attempt.inputTokens),
        outputTokens: finiteNumber(attempt.outputTokens),
        runDurationMs: finiteOptionalNumber(attempt.runDurationMs),
        prUrl: attempt.prUrl ?? '',
        commitSha: attempt.commitSha ?? '',
        createdAt: attempt.createdAt ?? '',
        updatedAt: attempt.updatedAt ?? '',
      })),
      runnableTaskIds: data.runnableTaskIds ?? [],
    },
  };
}

export async function listAttemptActivities(
  token: string,
  attemptId: string,
  startingAfter?: string,
  includePayload = false
): Promise<AttemptActivityPage> {
  const query = new URLSearchParams({
    limit: String(ATTEMPT_ACTIVITY_PAGE_SIZE),
  });
  if (startingAfter) query.set('startingAfter', startingAfter);
  if (includePayload) query.set('includePayload', 'true');
  const response = await adminFetch(
    token,
    `${ADMIN_ATTEMPTS_PATH}/${encodeURIComponent(attemptId)}/activities?${query}`
  );
  if (!response.ok) {
    throw new Error(
      `Attempt activity request failed with HTTP ${response.status}`
    );
  }
  const data = (await response.json()) as AttemptActivityPageWire;
  return {
    activities: (data.activities ?? []).map((activity) => ({
      activityId: activity.activityId ?? '',
      attemptId: activity.attemptId ?? '',
      taskId: activity.taskId ?? '',
      sequence: Number(activity.sequence ?? '0'),
      activityType: activity.activityType ?? '',
      actor: activity.actor ?? '',
      producerId: activity.producerId ?? '',
      occurredAt: activity.occurredAt ?? '',
      recordedAt: activity.recordedAt ?? '',
      source: activity.source ? ATTEMPT_ACTIVITY_SOURCES[activity.source] : '',
      outcome: activity.outcome ?? '',
      parentEventId: activity.parentEventId ?? '',
      traceId: activity.traceId ?? '',
      spanId: activity.spanId ?? '',
      captureLevel: activity.captureLevel
        ? ATTEMPT_ACTIVITY_CAPTURE_LEVELS[activity.captureLevel]
        : '',
      schemaVersion: activity.schemaVersion ?? 0,
      payload: activity.payload ?? '',
      artifactUri: activity.artifactUri ?? '',
      artifactSha256: activity.artifactSha256 ?? '',
      redactionPolicy: activity.redactionPolicy ?? '',
      redacted: activity.redacted ?? false,
      retainUntil: activity.retainUntil ?? '',
    })),
    hasMore: data.hasMore ?? false,
    nextCursor: data.nextCursor ?? '',
  };
}

function attemptActivityExportFromWire(
  value: AttemptActivityExportWire
): AttemptActivityExport {
  return {
    exportId: value.exportId ?? '',
    attemptId: value.attemptId ?? '',
    state: value.state ?? ATTEMPT_ACTIVITY_EXPORT_STATES.PENDING,
    includePayload: value.includePayload ?? false,
    highWaterSequence: finiteNumber(value.highWaterSequence),
    requestedAt: value.requestedAt ?? '',
    expiresAt: value.expiresAt ?? '',
    ...(value.failureCode ? { failureCode: value.failureCode } : {}),
    ...(value.completedAt ? { completedAt: value.completedAt } : {}),
  };
}

export async function requestAttemptActivityExport(
  token: string,
  attemptId: string,
  operationId: string,
  signal?: AbortSignal
): Promise<AttemptActivityExport> {
  const response = await adminFetch(
    token,
    `${ADMIN_ATTEMPTS_PATH}/${encodeURIComponent(attemptId)}/activity-exports`,
    {
      method: 'POST',
      body: JSON.stringify({ operationId, includePayload: true }),
      ...(signal ? { signal } : {}),
    }
  );
  if (!response.ok) {
    throw new Error(
      `Attempt activity export request failed with HTTP ${response.status}`
    );
  }
  return attemptActivityExportFromWire(
    (await response.json()) as AttemptActivityExportWire
  );
}

export async function getAttemptActivityExport(
  token: string,
  attemptId: string,
  exportId: string,
  signal?: AbortSignal
): Promise<AttemptActivityExport> {
  const response = await adminFetch(
    token,
    `${ADMIN_ATTEMPTS_PATH}/${encodeURIComponent(attemptId)}/activity-exports/${encodeURIComponent(exportId)}`,
    signal ? { signal } : undefined
  );
  if (!response.ok) {
    throw new Error(
      `Attempt activity export status failed with HTTP ${response.status}`
    );
  }
  return attemptActivityExportFromWire(
    (await response.json()) as AttemptActivityExportWire
  );
}

export async function readAttemptActivityExport(
  token: string,
  attemptId: string,
  exportId: string,
  signal?: AbortSignal
): Promise<string> {
  const response = await adminFetch(
    token,
    `${ADMIN_ATTEMPTS_PATH}/${encodeURIComponent(attemptId)}/activity-exports/${encodeURIComponent(exportId)}/content`,
    signal ? { signal } : undefined
  );
  if (!response.ok) {
    throw new Error(
      `Attempt activity export content failed with HTTP ${response.status}`
    );
  }
  return response.text();
}

export async function transitionAttempt(
  token: string,
  attemptId: string,
  transition: AttemptTransition
): Promise<void> {
  const response = await adminFetch(
    token,
    `${ADMIN_ATTEMPTS_PATH}/${encodeURIComponent(attemptId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(transition),
    }
  );
  if (response.status === ADMIN_HTTP_STATUS.CONFLICT) {
    const payload = (await response.json()) as {
      error?: string;
      message?: string;
    };
    if (payload.error === 'failed_precondition') {
      throw Object.assign(
        new Error(payload.message ?? 'Attempt transition conflicted'),
        { status: ADMIN_HTTP_STATUS.CONFLICT, code: payload.error }
      );
    }
    throw new Error('Attempt transition failed with HTTP 409');
  }
  if (!response.ok) {
    throw new Error(`Attempt transition failed with HTTP ${response.status}`);
  }
}

export async function listHumanReviewRequests(
  token: string,
  params: {
    subjectType?: string;
    state?: HumanReviewRequestState;
    priority?: string;
    decisionSource?: DecisionSource;
    sort?: HumanReviewSort;
    cursor?: string;
    limit?: number;
  } = {}
): Promise<HumanReviewRequestsResult> {
  const query = new URLSearchParams();
  if (params.subjectType) query.set('subjectType', params.subjectType);
  if (params.state) query.set('state', params.state);
  if (params.priority) query.set('priority', params.priority);
  if (params.decisionSource) query.set('decisionSource', params.decisionSource);
  if (params.sort) query.set('sort', params.sort);
  if (params.cursor) query.set('startingAfter', params.cursor);
  if (params.limit) query.set('limit', String(params.limit));

  const path = `${ADMIN_HUMAN_REVIEW_REQUESTS_PATH}${query.size ? `?${query}` : ''}`;
  const response = await adminFetch(token, path);
  if (response.status === 404) {
    return { status: 'disabled' };
  }
  if (!response.ok) {
    throw new Error(
      `Human-review request list failed with HTTP ${response.status}`
    );
  }
  return {
    status: 'available',
    data: (await response.json()) as HumanReviewRequestsResponse,
  };
}

export async function getHumanReviewRequest(
  token: string,
  requestId: string
): Promise<HumanReviewRequestDetailResult> {
  const response = await adminFetch(
    token,
    `${ADMIN_HUMAN_REVIEW_REQUESTS_PATH}/${encodeURIComponent(requestId)}`
  );
  if (response.status === 404) {
    return { status: 'disabled' };
  }
  if (!response.ok) {
    throw new Error(
      `Human-review request detail failed with HTTP ${response.status}`
    );
  }
  return {
    status: 'available',
    data: (await response.json()) as HumanReviewRequestDetail,
  };
}

export async function createHumanReviewDecision(
  token: string,
  body: {
    requestId: string;
    result: HumanReviewDecisionResult;
    reason: string;
    expectedSequence: number;
  }
): Promise<HumanReviewDecisionResponse> {
  const response = await adminFetch(
    token,
    `${ADMIN_HUMAN_REVIEW_REQUESTS_PATH}/${encodeURIComponent(body.requestId)}/decisions`,
    {
      method: 'POST',
      body: JSON.stringify({
        result: body.result,
        reason: body.reason,
        expectedSequence: body.expectedSequence,
      }),
    }
  );
  if (response.status === 409) {
    const payload = (await response.json()) as {
      error?: string;
      message?: string;
      request?: HumanReviewRequest;
    };
    if (
      payload.error === 'sequence_conflict' ||
      payload.error === 'request_expired'
    ) {
      throw Object.assign(
        new Error(payload.message ?? 'Human-review decision conflicted'),
        {
          status: 409,
          code: payload.error,
          request: payload.request,
        }
      );
    }
    throw new Error('Human-review decision failed with HTTP 409');
  }
  if (!response.ok) {
    throw new Error(
      `Human-review decision failed with HTTP ${response.status}`
    );
  }
  return (await response.json()) as HumanReviewDecisionResponse;
}

export type HumanReviewDecisionConflict = {
  status: 409;
  code: 'sequence_conflict';
  request?: HumanReviewRequest;
};

export type ExpiredReadOnlyConflict = {
  status: 409;
  code: 'request_expired';
  request?: HumanReviewRequest;
};

export type AttemptTransitionConflict = {
  status: 409;
  code: 'failed_precondition';
};

export function isAttemptTransitionConflict(
  error: unknown
): error is AttemptTransitionConflict {
  if (typeof error !== 'object' || error === null) return false;
  if (!('status' in error) || !('code' in error)) return false;
  return error.status === 409 && error.code === 'failed_precondition';
}

export function isHumanReviewDecisionConflict(
  error: unknown
): error is HumanReviewDecisionConflict {
  if (typeof error !== 'object' || error === null) return false;
  if (!('status' in error) || !('code' in error)) return false;
  return error.status === 409 && error.code === 'sequence_conflict';
}

export function isExpiredReadOnlyConflict(
  error: unknown
): error is ExpiredReadOnlyConflict {
  if (typeof error !== 'object' || error === null) return false;
  if (!('status' in error) || !('code' in error)) return false;
  return error.status === 409 && error.code === 'request_expired';
}
